import { useCallback, useEffect, useRef, useState } from 'react'

import type { CourseStep } from '../core/course.js'
import type { TimerLevel } from '../core/player.js'
import styles from './QuestionScreen.module.css'

/**
 * Obrazovka otázky — klasické odpovědi i klikací hotspoty.
 *
 * Vrstvené rozvržení: scéna vespod, otázka a odpovědi nad ní.
 * Podrobná rozvaha o zónách je v README, sekce „Rozvržení obrazovky".
 *
 * Rozdíly proti původní aplikaci:
 *  - o rozvržení rozhoduje CSS, ne pozice dopočítané z `window.innerWidth`
 *  - viditelnost nezávisí na animaci (původní `onLoad` na <div> se nikdy
 *    nespustil a otázka i odpovědi zůstaly vysunuté mimo obrazovku)
 *  - odpovědi jsou <button> v <ul> → ovladatelné klávesnicí i čtečkou
 *  - hotspoty se pozicují v % skutečně vykreslené plochy obrázku,
 *    takže sedí na každém rozlišení i po otočení telefonu
 */

export interface QuestionScreenProps {
  step: CourseStep
  /** Zvolená odpověď (1-based) po zodpovězení, jinak null. */
  answered: { selected: number; correct: boolean } | null
  timer: TimerLevel
  onAnswer: (selected: number) => void
  language: 'cze' | 'eng'
}

const TEXT = {
  cze: {
    correct: 'Správně',
    wrong: 'Špatně',
    question: 'Otázka',
    area: 'Oblast',
    imageFailed: 'Obrázek se nepodařilo načíst. Vyberte odpověď ze seznamu.',
  },
  eng: {
    correct: 'Correct',
    wrong: 'Incorrect',
    question: 'Question',
    area: 'Area',
    imageFailed: 'The image could not be loaded. Choose an answer from the list.',
  },
} as const

export function QuestionScreen({
  step,
  answered,
  timer,
  onAnswer,
  language,
}: QuestionScreenProps): React.ReactElement {
  const t = TEXT[language]
  const isHighlight = step.kind === 'highlight'

  /*
   * Nenačtený obrázek u klikací otázky byl slepá ulička: hotspoty se
   * pozicují podle skutečné plochy obrázku, takže bez něj se nevykreslí
   * NIC klikatelného — a protože highlight nemá textové odpovědi, uživatel
   * mohl kurz jen ukončit. Při chybě se proto nabídnou náhradní tlačítka.
   */
  const [imageFailed, setImageFailed] = useState(false)
  useEffect(() => setImageFailed(false), [step.position, step.imageUrl])

  const hotspotFallback = isHighlight && (imageFailed || step.imageUrl === '')

  return (
    <section className={styles.screen} aria-label={t.question}>
      {/* Scéna leží úplně vespod — nemá jak nic odsunout ani zakrýt */}
      {step.imageUrl !== '' && (
        <ImageStage
          src={step.imageUrl}
          step={step}
          disabled={answered !== null}
          onPick={onAnswer}
          showHotspots={isHighlight && !imageFailed}
          onImageError={() => setImageFailed(true)}
        />
      )}

      <div className={styles.questionBar}>
        <h1 className={styles.question}>{step.question}</h1>
      </div>

      {hotspotFallback && step.hotspots.length > 0 && (
        <div className={styles.answerBar}>
          <p className={styles.fallbackNote} role="status">{t.imageFailed}</p>
          <ul className={styles.answers} data-testid="answers-fallback">
            {step.hotspots.map((_, i) => {
              const value = i + 1
              return (
                <li key={`${step.position}-area-${value}`}>
                  <button
                    type="button"
                    className={styles.answer}
                    disabled={answered !== null}
                    onClick={() => onAnswer(value)}
                  >
                    {t.area} {value}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {!isHighlight && step.answers.length > 0 && (
        <div className={styles.answerBar}>
          <ul className={styles.answers} data-testid="answers">
            {step.answers.map((answer, i) => {
              const value = i + 1
              const isCorrect = value === step.correctAnswer
              const chosen = answered?.selected === value

              let cls = styles.answer
              if (answered) {
                if (isCorrect) cls += ` ${styles.correct}`
                else if (chosen) cls += ` ${styles.chosenWrong}`
              }

              return (
                <li key={`${step.position}-${value}`}>
                  <button
                    type="button"
                    className={cls}
                    disabled={answered !== null}
                    aria-pressed={chosen}
                    onClick={() => onAnswer(value)}
                  >
                    {answer}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {timer !== 'none' && (
        <div
          className={`${styles.timer} ${timer === 'over' ? styles.timerOver : styles.timerWarning}`}
          aria-hidden="true"
        >
          ⏱
        </div>
      )}

      {/*
        Vyhodnocení odpovědi — jen verdikt, nic víc.
        NENÍ to tlačítko: pokračuje se samo, takže tu není co odklikávat.
        Dřívější „Klepnutím přeskočíte" byl pozůstatek po berličce z původní
        aplikace, kde kliknutí muselo vynutit překreslení.
      */}
      {answered && (
        <div
          className={`${styles.tint} ${answered.correct ? styles.tintCorrect : styles.tintWrong}`}
          role="status"
          aria-live="polite"
          data-testid="feedback"
        >
          <strong className={styles.verdict}>
            {answered.correct ? `✓ ${t.correct}` : `✕ ${t.wrong}`}
          </strong>
        </div>
      )}
    </section>
  )
}

interface ImageStageProps {
  src: string
  step: CourseStep
  disabled: boolean
  showHotspots: boolean
  onPick: (value: number) => void
  /** Obrázek se nenačetl — volající nabídne náhradní odpovědi. */
  onImageError: () => void
}

/**
 * Obrázek otázky a nad ním vrstva hotspotů.
 *
 * Hotspoty se pozicují vůči SKUTEČNÉ vykreslené ploše obrázku (ne vůči oknu),
 * takže sedí i po změně velikosti, otočení telefonu nebo na jiném poměru stran.
 */
function ImageStage({
  src,
  step,
  disabled,
  showHotspots,
  onPick,
  onImageError,
}: ImageStageProps): React.ReactElement {
  const imgRef = useRef<HTMLImageElement>(null)
  const [box, setBox] = useState<{ width: number; height: number } | null>(null)

  /**
   * Skutečně vykreslená plocha obrázku.
   *
   * Element vyplňuje celý kontejner, ale `object-fit: contain` uvnitř nechává
   * prázdné pruhy. Hotspoty musí sedět na obrázek, ne na kontejner —
   * proto se plocha dopočítá z poměru stran.
   */
  const measure = useCallback((): void => {
    const el = imgRef.current
    if (!el || !el.naturalWidth || !el.naturalHeight) return

    const { width, height } = el.getBoundingClientRect()
    if (width === 0 || height === 0) return

    const natural = el.naturalWidth / el.naturalHeight
    const painted =
      width / height > natural
        ? { width: height * natural, height }
        : { width, height: width / natural }

    setBox(painted)
  }, [])

  useEffect(() => {
    const el = imgRef.current
    if (!el) return undefined

    measure()

    // ResizeObserver místo window.resize — reaguje i na změnu layoutu
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [src, measure])

  return (
    <>
      <div className={styles.backdrop}>
        <img
          ref={imgRef}
          className={styles.image}
          src={src}
          alt=""
          // Bez hromadného přednačítání — obrázky se stahují až když jsou potřeba
          loading="eager"
          decoding="async"
          onLoad={measure}
          onError={onImageError}
        />
      </div>

      {showHotspots && box && (
        <div className={styles.hotspotLayer}>
          <div className={styles.hotspotFrame} style={{ width: box.width, height: box.height }}>
            {step.hotspots.map((hotspot, i) => {
              const value = i + 1
              // Rozměr hotspotu je poměrný k šířce obrázku, stejně jako v datech
              const scaleFactor = (box.width / 1000) * hotspot.scale
              return (
                <button
                  key={`${step.position}-hotspot-${value}`}
                  type="button"
                  className={styles.hotspot}
                  disabled={disabled}
                  aria-label={`Oblast ${value}`}
                  style={{
                    top: `${hotspot.posFromTopPercent}%`,
                    left: `${hotspot.posFromLeftPercent}%`,
                    width: hotspot.width * scaleFactor,
                    height: hotspot.height * scaleFactor,
                  }}
                  onClick={() => onPick(value)}
                >
                  <HotspotImage hotspot={hotspot} />
                </button>
              )
            })}
          </div>
        </div>
      )}
    </>
  )
}

function HotspotImage({
  hotspot,
}: {
  hotspot: CourseStep['hotspots'][number]
}): React.ReactElement {
  const [hover, setHover] = useState(false)
  const src = hover && hotspot.highlightImageUrl !== '' ? hotspot.highlightImageUrl : hotspot.baseImageUrl

  return (
    <img
      src={src}
      alt=""
      decoding="async"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      // Dotyk nemá hover — zvýraznění se ukáže při doteku
      onTouchStart={() => setHover(true)}
      onTouchEnd={() => setHover(false)}
    />
  )
}
